<template>
  <div class="">
    <div v-if="liveValidated || testValidated" class="absolute top-6 right-6">
      <el-dropdown trigger="click">
        <div class="px-4 py-2">
          <font-awesome-icon :icon="['fas', 'ellipsis-v']" />
        </div>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item v-if="!showApiKeyInput" icon="el-icon-plus" @click.native="addKeys">Add API Keys</el-dropdown-item>
          <el-dropdown-item icon="el-icon-delete" @click.native="deactivate">Clear Stripe Connection</el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>
    <div v-else class="absolute top-6 right-6">
      <el-dropdown trigger="click">
        <div class="px-4 py-2">
          <font-awesome-icon :icon="['fas', 'ellipsis-v']" />
        </div>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item icon="el-icon-plus" @click.native="showAdvancedSettings">Advanced Connection Settings</el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>
    <div v-loading="isLoading">
      <!-- <img class="h-8 my-6" :src="require(`@/assets/img/badge-stripe-verified.svg`)" alt="Churnkey is a stripe verified partner" /> -->
      <p class="header">Live Mode</p>
      <div class="pb-4">
        <div v-if="manualConnection">
          <!-- MANUAL MODE -->
          <div class="space-y-2 p-4">
            <p class="text-sm font-semibold text-gray-800">API Key</p>
            <el-input
              v-if="!editLiveApiKey && liveCredentials.apiKey"
              :value="liveCredentials.apiKey.slice(0, 7) + '...'"
              type="text"
              disabled
            ></el-input>
            <el-input v-if="editLiveApiKey || !liveCredentials.apiKey" v-model="liveApiKey" type="text" placeholder="live_YOUR_API_KEY"></el-input>
            <div class="mt-2">
              <el-button v-if="hasLiveApiKey && !editLiveApiKey" @click="checkLiveApiKey()" type="default" :loading="isLoadingApiLive"
                >Verify API key</el-button
              >
              <el-button v-if="hasLiveApiKey && !editLiveApiKey" @click="editLiveApiKey = true" type="primary">Edit Live API Key</el-button>
              <el-button v-if="editLiveApiKey" @click="editLiveApiKey = false" type="text">Cancel</el-button>
              <el-button v-if="!hasLiveApiKey || editLiveApiKey" @click="saveLiveApiKey" type="primary">Save</el-button>
              <el-button v-if="hasLiveApiKey && !editLiveApiKey" @click="removeApiKeys('live')" type="text" class="font-semibold">Delete</el-button>
            </div>
          </div>
        </div>
        <!-- END MANUAL MODE -->
        <div v-else>
          <!-- OAUTH MODE -->
          <!-- Live Mode Block | Connected -->
          <div v-if="liveValidated">
            <p class="text-lg font-bold mt-4 text-green-700 flex items-center space-x-2">
              <font-awesome-icon :icon="['fas', 'check-circle']" class="text-sm"></font-awesome-icon>
              <span>Active</span>
            </p>
            <p class="text-base text-brand-brown font-medium mt-2 mb-4">Click to confirm your integration is working correctly.</p>
            <div class="mt-1">
              <el-button @click="checkLiveMode()" :loading="isLoadingLive" type="default">Check Connection</el-button>
            </div>
            <div v-if="showApiKeyInput" class="my-4 space-y-2 bg-brand-sand rounded-md shadow-sm px-4 py-4">
              <p class="text-sm font-semibold text-gray-800">API Key</p>
              <p class="text-gray-500 mt-2 pb-1 subheader text-sm font-medium">
                If you use a 3rd party to create your customer subscriptions, you may need to add an API key so that Churnkey can update subscriptions
                on your behalf (apply discounts, pauses, and cancellations).
              </p>
              <el-input
                v-if="!editLiveApiKey && liveCredentials.apiKey"
                :value="liveCredentials.apiKey.slice(0, 7) + '...'"
                type="text"
                disabled
              ></el-input>
              <el-input v-if="editLiveApiKey || !liveCredentials.apiKey" v-model="liveApiKey" type="text" placeholder="live_YOUR_API_KEY"></el-input>
              <div class="mt-2">
                <el-button v-if="hasLiveApiKey && !editLiveApiKey" @click="checkLiveApiKey()" type="default" :loading="isLoadingApiLive"
                  >Verify API key</el-button
                >
                <el-button v-if="hasLiveApiKey && !editLiveApiKey" @click="editLiveApiKey = true" type="primary">Edit Live API Key</el-button>
                <el-button v-if="editLiveApiKey" @click="editLiveApiKey = false" type="text">Cancel</el-button>
                <el-button v-if="!hasLiveApiKey || editLiveApiKey" @click="saveLiveApiKey" type="primary">Save</el-button>
                <el-button v-if="hasLiveApiKey && !editLiveApiKey" @click="removeApiKeys('live')" type="text" class="font-semibold">Delete</el-button>
              </div>
            </div>
          </div>
          <!-- Live Mode Block | Not Connected -->
          <div v-if="!liveValidated">
            <p class="text-md font-bold text-gray-700 mt-4 flex items-center space-x-2">
              <font-awesome-layers class="text-xl">
                <font-awesome-icon :icon="['fas', 'circle']" class="text-brand-medium-blue" />
                <font-awesome-icon :icon="['fas', 'plug']" class="text-xs text-brand-blue" :class="'text-brand-blue'" />
              </font-awesome-layers>
              <span>Connect Your Account</span>
            </p>
            <p class="text-sm text-gray-600 mt-1">
              Connect your company's <span class="font-semibold">live Stripe account</span> in order to fully integrate Churnkey.
            </p>
            <div class="mt-4">
              <a :href="stripeConnectUrlLive" class="el-button text-md font-semibold py-3 el-button--primary"> Connect Stripe Live → </a>
            </div>
          </div>
        </div>
        <!-- end OAUTH MODE -->
      </div>
      <hr class="mb-6 border-brand-sand" />
      <p class="header">Test Mode</p>
      <div class="pb-4">
        <div v-if="manualConnection">
          <!-- MANUAL MODE -->
          <div class="space-y-2 p-4">
            <p class="text-sm font-semibold text-gray-700">API Key</p>
            <el-input
              v-if="!editTestApiKey && testCredentials.apiKey"
              :value="testCredentials.apiKey.slice(0, 7) + '...'"
              type="text"
              disabled
            ></el-input>
            <el-input v-if="editTestApiKey || !testCredentials.apiKey" v-model="testApiKey" type="text" placeholder="test_YOUR_API_KEY"></el-input>
            <div class="mt-2">
              <el-button v-if="hasTestApiKey && !editTestApiKey" @click="checkTestApiKey()" type="default" :loading="isLoadingApiTest"
                >Verify API key</el-button
              >
              <el-button v-if="hasTestApiKey && !editTestApiKey" @click="editTestApiKey = true" type="primary">Edit Test API key</el-button>
              <el-button v-if="editTestApiKey" @click="editTestApiKey = false" type="text">Cancel</el-button>
              <el-button v-if="!hasTestApiKey || editTestApiKey" @click="saveTestApiKey" type="primary">Save API key</el-button>
              <el-button v-if="hasTestApiKey && !editTestApiKey" @click="removeApiKeys('test')" type="text" class="font-semibold">Delete</el-button>
            </div>
          </div>
        </div>
        <!-- END MANUAL MODE -->
        <div v-else>
          <!-- OAUTH MODE -->

          <!-- Test Mode Block | Connected -->
          <div v-if="testValidated">
            <p class="text-lg font-bold mt-4 text-green-700 flex items-center space-x-2">
              <font-awesome-icon :icon="['fas', 'check-circle']" class="text-sm"></font-awesome-icon>
              <span>Active</span>
            </p>
            <p class="text-base text-brand-brown font-medium mt-2 mb-4">Click to confirm your integration is working correctly.</p>
            <div class="mt-1">
              <el-button @click="checkTestMode()" :loading="isLoadingTest" type="default">Check Connection</el-button>
            </div>
            <div v-if="showApiKeyInput" class="my-4 space-y-2 bg-brand-sand rounded-md shadow-sm px-4 py-4">
              <p class="text-sm font-semibold text-gray-700">API Key</p>
              <el-input
                v-if="!editTestApiKey && testCredentials.apiKey"
                :value="testCredentials.apiKey.slice(0, 7) + '...'"
                type="text"
                disabled
              ></el-input>
              <el-input v-if="editTestApiKey || !testCredentials.apiKey" v-model="testApiKey" type="text" placeholder="test_YOUR_API_KEY"></el-input>
              <div class="mt-2">
                <el-button v-if="hasTestApiKey && !editTestApiKey" @click="checkTestApiKey()" type="default" :loading="isLoadingApiTest"
                  >Verify API key</el-button
                >
                <el-button v-if="hasTestApiKey && !editTestApiKey" @click="editTestApiKey = true" type="primary">Edit Test API key</el-button>
                <el-button v-if="editTestApiKey" @click="editTestApiKey = false" type="text">Cancel</el-button>
                <el-button v-if="!hasTestApiKey || editTestApiKey" @click="saveTestApiKey" type="primary">Save API key</el-button>
                <el-button v-if="hasTestApiKey && !editTestApiKey" @click="removeApiKeys('test')" type="text" class="font-semibold">Delete</el-button>
              </div>
            </div>
          </div>
          <!-- Test Mode Block | Not Connected -->
          <div v-else>
            <p class="text-md font-bold text-gray-700 mt-4 flex items-center space-x-2">
              <font-awesome-layers class="text-3xl">
                <font-awesome-icon :icon="['fas', 'circle']" class="text-brand-medium-blue" />
                <font-awesome-icon :icon="['fas', 'plug']" class="text-base text-brand-blue" :class="'text-brand-blue'" />
              </font-awesome-layers>
              <span>Connect Your Account</span>
            </p>
            <p class="text-sm text-gray-600 mt-1">
              Connect to your company's <span class="font-semibold">test Stripe account</span> in order to test your Churnkey embed.
            </p>
            <div class="mt-4">
              <a :href="stripeConnectUrlTest" class="el-button text-md font-semibold py-3 el-button--primary"> Connect Stripe Test → </a>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Dialog: Advanced Connection Settings -->
    <el-dialog
      class="p-0"
      custom-class="container max-w-2xl rounded-lg px-0 py-0 no-padding"
      :visible.sync="advancedSettings"
      @closed="closedAdvancedSettings"
      :append-to-body="true"
    >
      <StripeAdvancedSettings :close="closeAdvancedSettings" />
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import OrgService from '@/api/org.service';
import AuthService from '@/api/auth.service';
import { SYNC_ORG } from '@/store/action-types';
import StripeAdvancedSettings from '@/components/Settings/SettingsConnectStripeAdvanced.vue';

const stripeClientIdLive = 'ca_I4LOcF9sJYgxdwmpNGPMn0buaBAdyh6Q';
const stripeClientIdTest = 'ca_I4LOYSW5co4HuYMTsK2TW9xH0x2ZQ5vP';

export default {
  name: 'StripeConnectStripe',
  components: {
    StripeAdvancedSettings,
  },
  data() {
    return {
      isLoading: true,
      isLoadingLive: false,
      isLoadingTest: false,
      isLoadingApiLive: false,
      isLoadingApiTest: false,
      testValidated: false,
      liveValidated: false,
      testApiKeyValidated: false,
      liveApiKeyValidated: false,
      showApiKeys: false,
      liveApiKey: '',
      testApiKey: '',
      editLiveApiKey: false,
      editTestApiKey: false,
      advancedSettings: false, // switch between OAuth and manual connection
      accountId: '',
    };
  },
  async created() {
    if (this.$route.query.code) {
      const authCode = this.$route.query.code;
      await this.stripeAuthCallback(authCode);
    }
    await this.$store.dispatch(SYNC_ORG);
    await this.testConnections();

    if (this.manualConnection) {
      console.log('manual mode');
      this.showApiKeys = true; // Show API keys if in Manual Connection mode
    }

    this.isLoading = false;
  },
  computed: {
    ...mapGetters(['orgProviderSettings']),
    manualConnection() {
      return this.orgProviderSettings?.manualConnection || false;
    },
    user() {
      return this.$store.state.user;
    },
    org() {
      return this.$store.state.org;
    },
    showApiKeyInput() {
      return this.showApiKeys || this.hasTestApiKey || this.hasLiveApiKey;
    },
    stripeRedirectUri() {
      // stripe requires https
      const origin = window.location.origin.replace('http:', 'https:');
      return window.encodeURIComponent(`${origin}${this.$route.path}`);
    },
    stripeConnectBaseUrl() {
      return `https://connect.stripe.com/oauth/authorize?response_type=code&scope=read_write&redirect_uri=${this.stripeRedirectUri}`;
    },
    stripeConnectUrlLive() {
      return `${this.stripeConnectBaseUrl}&client_id=${stripeClientIdLive}`;
    },
    stripeConnectUrlTest() {
      return `${this.stripeConnectBaseUrl}&client_id=${stripeClientIdTest}`;
    },
    hasTestApiKey() {
      return this.testCredentials.apiKey;
    },
    hasLiveApiKey() {
      return this.liveCredentials.apiKey;
    },
    testCredentials() {
      if (this.org?.secrets?.test) {
        return {
          apiKey: this.org.secrets.test.stripeApiKey,
        };
      }
      return {};
    },
    liveCredentials() {
      if (this.org?.secrets?.prod) {
        return {
          apiKey: this.org.secrets.prod.stripeApiKey,
        };
      }
      return {};
    },
    stripeAccountId() {
      return this.org.stripeAccountId;
    },
  },
  methods: {
    async testConnections() {
      try {
        await OrgService.testStripeConnect(this.user.org, 'test');
        this.testValidated = true;
      } catch {
        this.testValidated = false;
      }
      try {
        await OrgService.testStripeConnect(this.user.org, 'live');
        this.liveValidated = true;
      } catch {
        this.liveValidated = false;
      }
    },
    async deactivate() {
      this.isLoading = true;
      try {
        await OrgService.removeStripeConnection(this.user.org);
        this.$notify({
          type: 'success',
          duration: 2000,
          title: 'Successfully disconnected your Stripe account.',
        });
      } catch (e) {
        this.$notify({
          type: 'error',
          duration: 2000,
          title: 'There was an error removing your Stripe account connection.',
        });
      } finally {
        await this.$store.dispatch(SYNC_ORG);
        await this.testConnections();
        this.isLoading = false;
      }
    },
    async removeApiKeys(mode) {
      this.isLoading = true;
      try {
        if (mode === 'test') {
          await OrgService.removeStripeApiKey(this.user.org, 'test');
        } else if (mode === 'live') {
          await OrgService.removeStripeApiKey(this.user.org, 'live');
        }
        this.$notify({
          type: 'success',
          duration: 2000,
          title: `Successfully removed Stripe ${mode} key.`,
        });
      } catch (e) {
        this.$notify({
          type: 'error',
          duration: 2000,
          title: `There was an error removing Stripe ${mode} key.`,
        });
      } finally {
        await this.$store.dispatch(SYNC_ORG);
        this.isLoading = false;
      }
    },
    showAdvancedSettings() {
      this.advancedSettings = true;
    },
    closeAdvancedSettings() {
      this.advancedSettings = false;
    },
    closedAdvancedSettings() {
      const settings = this.orgProviderSettings;
      this.showApiKeys = true; // Show API keys if in Manual Connection mode
    },
    addKeys() {
      this.showApiKeys = true;
    },
    async stripeAuthCallback(authCode) {
      try {
        await AuthService.stripeAuthCallback(authCode, this.user._id);
        this.$notify({ title: 'Stripe Connected', type: 'success' });
        // update url to strip query params
        this.$router.push('/settings/billing-provider');
      } catch (error) {
        console.log(error);
        this.$notify({ title: 'Stripe Connection Failed', type: 'error' });
      }
    },
    async checkTestApiKey() {
      this.isLoadingApiTest = true;
      try {
        await OrgService.testStripeApiKey(this.user.org, 'test');
        this.testApiKeyValidated = true;
        this.$notify({ title: 'Test API Key Connected', type: 'success' });
      } catch (error) {
        this.testApiKeyValidated = false;
        this.$notify({ title: 'Error', message: 'Invalid Test API Key', type: 'error' });
        console.log('Error', error);
      }
      this.isLoadingApiTest = false;
    },
    async checkLiveApiKey() {
      this.isLoadingApiLive = true;
      try {
        await OrgService.testStripeApiKey(this.user.org, 'live');
        this.liveApiKeyValidated = true;
        this.$notify({ title: 'Live API Key Connected', type: 'success' });
      } catch (error) {
        this.liveApiKeyValidated = false;
        this.$notify({ title: 'Error', message: 'Invalid Live API Key', type: 'error' });
        console.log('Error', error);
      }
      this.isLoadingApiLive = false;
    },
    async checkTestMode() {
      this.isLoadingTest = true;
      try {
        await OrgService.testStripeConnect(this.user.org, 'test');
        this.testValidated = true;
        this.$notify({ title: 'Test Mode Connected', type: 'success' });
      } catch (error) {
        this.testValidated = false;
        this.$notify({ title: 'Error', message: 'Stripe Connection Failed', type: 'error' });
        console.log('Error', error);
      }
      this.isLoadingTest = false;
    },
    async checkLiveMode() {
      this.isLoadingLive = true;
      try {
        await OrgService.testStripeConnect(this.user.org, 'live');
        this.liveValidated = true;
        this.$notify({ title: 'Live Mode Connected', type: 'success' });
      } catch (error) {
        this.liveValidated = false;

        this.$notify({ title: 'Error', message: 'Stripe Connection Failed', type: 'error' });
        console.log('Error', error);
      }
      this.isLoadingLive = false;
    },
    async saveLiveApiKey() {
      this.saveApiKey('live');
    },
    async saveTestApiKey() {
      this.saveApiKey('test');
    },
    async saveApiKey(mode) {
      console.log('Saving API key...', this.testApiKey);
      try {
        const secrets = {};
        this.isLoading = true;
        if (this.liveApiKey && mode === 'live') {
          secrets.prod = {
            stripeApiKey: this.liveApiKey,
          };
        }
        if (this.testApiKey && mode === 'test') {
          secrets.test = {
            stripeApiKey: this.testApiKey,
          };
        }
        const result = await OrgService.saveStripeApiKeys(this.user.org, secrets);
        this.$notify({ title: 'Stripe API Keys Saved', type: 'success' });
        await this.$store.dispatch(SYNC_ORG);
        if (mode === 'live') {
          this.editLiveApiKey = false;
        } else {
          this.editTestApiKey = false;
        }
      } catch (error) {
        console.log('Error', error);
        this.$notify({ title: 'Stripe API Keys Update Failed', type: 'error' });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
